/**
 * Sales Firestore Service
 * Direct Firestore SDK calls for sales CRUD and aggregation operations
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  serverTimestamp,
  Timestamp,
  documentId
} from 'firebase/firestore';
import { db } from '../../config/firebase';

// Helper to convert Firestore timestamp to Date
const convertTimestamp = (timestamp) => {
  if (!timestamp) return null;
  if (timestamp.toDate) {
    return timestamp.toDate();
  }
  if (timestamp instanceof Date) {
    return timestamp;
  }
  return new Date(timestamp);
};

// Helper function to get week information
const getWeekInfo = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const startOfYear = new Date(year, 0, 1);
  const days = Math.floor((d - startOfYear) / (24 * 60 * 60 * 1000));
  const weekNumber = Math.ceil((days + startOfYear.getDay() + 1) / 7);

  return {
    year,
    weekNumber,
    weekKey: `${year}-W${weekNumber.toString().padStart(2, '0')}`
  };
};

// Helper to batch fetch products to avoid N+1 queries
const batchFetchProducts = async (productIds) => {
  if (!productIds || productIds.length === 0) {
    return new Map();
  }

  const uniqueProductIds = [...new Set(productIds.filter(Boolean))];

  if (uniqueProductIds.length === 0) {
    return new Map();
  }

  try {
    // Fetch products in batches of 10 (Firestore 'in' query limit)
    const productMap = new Map();
    const chunks = [];
    for (let i = 0; i < uniqueProductIds.length; i += 10) {
      chunks.push(uniqueProductIds.slice(i, i + 10));
    }

    for (const chunk of chunks) {
      const productsRef = collection(db, 'products');
      const q = query(productsRef, where(documentId(), 'in', chunk));
      const snapshot = await getDocs(q);

      snapshot.docs.forEach((doc) => {
        productMap.set(doc.id, { id: doc.id, ...doc.data() });
      });
    }

    return productMap;
  } catch (err) {
    console.error('Error batch fetching products:', err);
    return new Map();
  }
};

// Helper to convert document to sale object
const docToSale = (doc, productData = null) => {
  const data = doc.data();
  return {
    id: doc.id,
    ...data,
    date: convertTimestamp(data.date),
    createdAt: convertTimestamp(data.createdAt),
    updatedAt: convertTimestamp(data.updatedAt),
    product: productData
  };
};

/**
 * Get all sales with optional filters
 */
export const getAll = async (options = {}) => {
  try {
    const salesRef = collection(db, 'sales');
    let constraints = [];

    if (options.collectedBy) {
      constraints.push(where('collectedBy', '==', options.collectedBy));
    }

    if (options.startDate && options.endDate) {
      constraints.push(where('date', '>=', Timestamp.fromDate(new Date(options.startDate))));
      constraints.push(where('date', '<=', Timestamp.fromDate(new Date(options.endDate))));
    }

    if (options.productType) {
      constraints.push(where('productType', '==', options.productType));
    }

    if (options.productId) {
      constraints.push(where('productId', '==', options.productId));
    }

    constraints.push(orderBy('date', 'desc'));

    if (options.limit) {
      constraints.push(firestoreLimit(parseInt(options.limit)));
    }

    const q = query(salesRef, ...constraints);
    const snapshot = await getDocs(q);

    // Batch fetch products
    const productIds = snapshot.docs.map((doc) => doc.data().productId).filter(Boolean);
    const productMap = await batchFetchProducts(productIds);

    const sales = snapshot.docs.map((doc) => {
      const data = doc.data();
      const productData = data.productId ? productMap.get(data.productId) || null : null;
      return docToSale(doc, productData);
    });

    return sales;
  } catch (error) {
    console.error('Error fetching sales:', error);
    throw error;
  }
};

/**
 * Get current week's sales for dashboard
 */
export const getCurrentWeek = async (options = {}) => {
  try {
    const currentWeekInfo = getWeekInfo(new Date());
    const salesRef = collection(db, 'sales');
    let constraints = [
      where('year', '==', currentWeekInfo.year),
      where('weekNumber', '==', currentWeekInfo.weekNumber),
      orderBy('date', 'desc')
    ];

    if (options.collectedBy) {
      constraints.push(where('collectedBy', '==', options.collectedBy));
    }

    if (options.productType) {
      constraints.push(where('productType', '==', options.productType));
    }

    if (options.productId) {
      constraints.push(where('productId', '==', options.productId));
    }

    if (options.limit) {
      constraints.push(firestoreLimit(parseInt(options.limit)));
    }

    const q = query(salesRef, ...constraints);
    const snapshot = await getDocs(q);

    // Batch fetch products
    const productIds = snapshot.docs.map((doc) => doc.data().productId).filter(Boolean);
    const productMap = await batchFetchProducts(productIds);

    const sales = snapshot.docs.map((doc) => {
      const data = doc.data();
      const productData = data.productId ? productMap.get(data.productId) || null : null;
      return docToSale(doc, productData);
    });

    return {
      sales,
      weekInfo: currentWeekInfo
    };
  } catch (error) {
    console.error('Error fetching current week sales:', error);
    throw error;
  }
};

/**
 * Get weekly sales summary
 */
export const getWeekly = async (year = null, weekNumber = null) => {
  try {
    const salesRef = collection(db, 'sales');
    let constraints = [orderBy('date', 'desc')];

    if (year) {
      constraints = [where('year', '==', parseInt(year)), ...constraints];
    }

    if (weekNumber) {
      constraints = [where('weekNumber', '==', parseInt(weekNumber)), ...constraints];
    }

    const q = query(salesRef, ...constraints);
    const snapshot = await getDocs(q);

    // Batch fetch products
    const productIds = snapshot.docs.map((doc) => doc.data().productId).filter(Boolean);
    const productMap = await batchFetchProducts(productIds);

    const sales = snapshot.docs.map((doc) => {
      const data = doc.data();
      const productData = data.productId ? productMap.get(data.productId) || null : null;
      return docToSale(doc, productData);
    });

    // Group by week
    const weeklyData = {};
    sales.forEach((sale) => {
      const weekKey = sale.weekKey || getWeekInfo(sale.date).weekKey;
      if (!weeklyData[weekKey]) {
        weeklyData[weekKey] = {
          weekKey,
          year: sale.year,
          weekNumber: sale.weekNumber,
          sales: [],
          totalAmount: 0,
          totalSales: 0,
          startDate: sale.date,
          endDate: sale.date
        };
      }

      weeklyData[weekKey].sales.push(sale);
      weeklyData[weekKey].totalAmount += parseFloat(sale.amount || 0);
      weeklyData[weekKey].totalSales += 1;

      const saleDate = new Date(sale.date);
      if (saleDate < new Date(weeklyData[weekKey].startDate)) {
        weeklyData[weekKey].startDate = sale.date;
      }
      if (saleDate > new Date(weeklyData[weekKey].endDate)) {
        weeklyData[weekKey].endDate = sale.date;
      }
    });

    return Object.values(weeklyData).sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
  } catch (error) {
    console.error('Error fetching weekly sales:', error);
    throw error;
  }
};

/**
 * Get sales by specific week
 */
export const getByWeek = async (year, weekNumber, options = {}) => {
  try {
    const salesRef = collection(db, 'sales');
    let constraints = [
      where('year', '==', parseInt(year)),
      where('weekNumber', '==', parseInt(weekNumber)),
      orderBy('date', 'desc')
    ];

    if (options.collectedBy) {
      constraints.push(where('collectedBy', '==', options.collectedBy));
    }

    if (options.productType) {
      constraints.push(where('productType', '==', options.productType));
    }

    if (options.productId) {
      constraints.push(where('productId', '==', options.productId));
    }

    if (options.limit) {
      constraints.push(firestoreLimit(parseInt(options.limit)));
    }

    const q = query(salesRef, ...constraints);
    const snapshot = await getDocs(q);

    // Batch fetch products
    const productIds = snapshot.docs.map((doc) => doc.data().productId).filter(Boolean);
    const productMap = await batchFetchProducts(productIds);

    const sales = snapshot.docs.map((doc) => {
      const data = doc.data();
      const productData = data.productId ? productMap.get(data.productId) || null : null;
      return docToSale(doc, productData);
    });

    return {
      sales,
      weekInfo: {
        year: parseInt(year),
        weekNumber: parseInt(weekNumber),
        totalAmount: sales.reduce((sum, sale) => sum + parseFloat(sale.amount || 0), 0),
        totalSales: sales.length
      }
    };
  } catch (error) {
    console.error('Error fetching sales by week:', error);
    throw error;
  }
};

/**
 * Create a new sale
 */
export const create = async (saleData) => {
  try {
    const saleDate = saleData.date || new Date();
    const weekInfo = getWeekInfo(saleDate);

    const saleDoc = {
      customerName: saleData.customerName?.trim() || '',
      customerPhone: saleData.customerPhone?.trim() || '',
      location: saleData.location?.trim() || '',
      productType: saleData.productType?.trim() || '',
      productId: saleData.productId || null,
      quantity: saleData.quantity || 1,
      amount: parseFloat(saleData.amount) || 0,
      unitPrice: saleData.unitPrice === '' || saleData.unitPrice == null ? null : parseFloat(saleData.unitPrice),
      listPrice: saleData.listPrice == null ? null : parseFloat(saleData.listPrice),
      collectedBy: saleData.collectedBy?.trim() || '',
      notes: saleData.notes?.trim() || '',
      date: Timestamp.fromDate(new Date(saleDate)),
      weekNumber: weekInfo.weekNumber,
      year: weekInfo.year,
      weekKey: weekInfo.weekKey,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const salesRef = collection(db, 'sales');
    const docRef = await addDoc(salesRef, saleDoc);

    // Get product data if productId exists
    let productData = null;
    if (saleData.productId) {
      try {
        const productRef = doc(db, 'products', saleData.productId);
        const productDoc = await getDoc(productRef);
        if (productDoc.exists()) {
          productData = { id: productDoc.id, ...productDoc.data() };
        }
      } catch (err) {
        console.error('Error fetching product:', err);
      }
    }

    return {
      id: docRef.id,
      ...saleDoc,
      date: new Date(saleDate),
      createdAt: new Date(),
      updatedAt: new Date(),
      product: productData
    };
  } catch (error) {
    console.error('Error creating sale:', error);
    throw error;
  }
};

/**
 * Update a sale
 */
export const update = async (saleId, updateData) => {
  try {
    const saleRef = doc(db, 'sales', saleId);
    const saleDate = updateData.date || new Date();
    const weekInfo = getWeekInfo(saleDate);

    const updateDocData = {
      customerName: updateData.customerName?.trim() || '',
      customerPhone: updateData.customerPhone?.trim() || '',
      location: updateData.location?.trim() || '',
      productType: updateData.productType?.trim() || '',
      productId: updateData.productId || null,
      quantity: updateData.quantity || 1,
      amount: parseFloat(updateData.amount) || 0,
      unitPrice: updateData.unitPrice === '' || updateData.unitPrice == null ? null : parseFloat(updateData.unitPrice),
      listPrice: updateData.listPrice == null ? null : parseFloat(updateData.listPrice),
      collectedBy: updateData.collectedBy?.trim() || '',
      notes: updateData.notes?.trim() || '',
      date: Timestamp.fromDate(new Date(saleDate)),
      weekNumber: weekInfo.weekNumber,
      year: weekInfo.year,
      weekKey: weekInfo.weekKey,
      updatedAt: serverTimestamp()
    };

    await updateDoc(saleRef, updateDocData);

    // Return updated document with product data
    const updatedDoc = await getDoc(saleRef);
    const data = updatedDoc.data();

    let productData = null;
    if (data.productId) {
      try {
        const productRef = doc(db, 'products', data.productId);
        const productDoc = await getDoc(productRef);
        if (productDoc.exists()) {
          productData = { id: productDoc.id, ...productDoc.data() };
        }
      } catch (err) {
        console.error('Error fetching product:', err);
      }
    }

    return docToSale(updatedDoc, productData);
  } catch (error) {
    console.error('Error updating sale:', error);
    throw error;
  }
};

/**
 * Delete a sale
 */
export const remove = async (saleId) => {
  try {
    const saleRef = doc(db, 'sales', saleId);
    await deleteDoc(saleRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting sale:', error);
    throw error;
  }
};

/**
 * Get sales statistics
 */
export const getStats = async (options = {}) => {
  try {
    const salesRef = collection(db, 'sales');
    let constraints = [];

    if (options.startDate && options.endDate) {
      constraints.push(where('date', '>=', Timestamp.fromDate(new Date(options.startDate))));
      constraints.push(where('date', '<=', Timestamp.fromDate(new Date(options.endDate))));
    }

    const q = constraints.length > 0 ? query(salesRef, ...constraints) : query(salesRef);
    const snapshot = await getDocs(q);

    // Batch fetch products
    const productIds = snapshot.docs.map((doc) => doc.data().productId).filter(Boolean);
    const productMap = await batchFetchProducts(productIds);

    const sales = snapshot.docs.map((doc) => {
      const data = doc.data();
      const productData = data.productId ? productMap.get(data.productId) || null : null;
      return { ...data, product: productData };
    });

    // Calculate stats
    const totalAmount = sales.reduce((sum, sale) => sum + parseFloat(sale.amount || 0), 0);
    const totalSales = sales.length;
    const averageAmount = totalSales > 0 ? totalAmount / totalSales : 0;

    // Calculate most sold product by quantity
    const productCounts = {};
    sales.forEach((sale) => {
      const productName = sale.product?.name || sale.productType || 'Unknown Product';
      const quantity = sale.quantity || 1;
      productCounts[productName] = (productCounts[productName] || 0) + quantity;
    });

    let mostSoldProduct = 'N/A';
    let maxCount = 0;
    for (const [product, count] of Object.entries(productCounts)) {
      if (count > maxCount) {
        maxCount = count;
        mostSoldProduct = product;
      }
    }

    return {
      totalAmount,
      totalSales,
      averageAmount,
      mostSoldProduct
    };
  } catch (error) {
    console.error('Error getting sales stats:', error);
    throw error;
  }
};

// Export as object for compatibility with API pattern
const salesService = {
  getAll,
  getCurrentWeek,
  getWeekly,
  getByWeek,
  create,
  update,
  delete: remove,
  getStats
};

export default salesService;
